<template>
  <div class="admin-subscriptions">
    <div v-if="!embedded" class="admin-subscriptions__topbar">
      <div class="admin-subscriptions__actions">
          <hs-button
            variant="primary"
            icon="plus"
            @click="openCreatePlan"
          >
            New Plan
          </hs-button>
          <hs-button
            variant="secondary"
            :loading="loadingPlans"
            icon="refresh-cw"
            @click="refreshData"
          >
            Refresh
          </hs-button>
      </div>
    </div>

    <div class="admin-subscriptions__body">
      <section class="admin-subscriptions__stats">
        <hs-metric-card
          v-for="stat in summaryStats"
          :key="stat.label"
          :label="stat.label"
          :value="stat.value"
          :description="stat.caption"
          :icon="stat.icon"
          :tone="stat.tone"
        />
      </section>

      <div class="admin-subscriptions__panel">
        <hs-tabs :tabs="tabs" v-model="activeTab" />
        <div class="admin-subscriptions__tab-panel">
          <!-- Plans -->
          <div v-if="activeTab === 'plans'">
            <hs-table-shell
              title="Subscription plans"
              description="Create, review, and update the plans available for store and group assignments."
            >
              <template #filters>
                <div class="admin-subscriptions__toolbar">
                  <div class="admin-subscriptions__filters">
                    <hs-input
                      v-model="planSearch"
                      type="search"
                      icon="search"
                      placeholder="Search plans by name or description..."
                    />
                    <hs-select
                      v-model="planStatusFilter"
                      class="min-w-[180px]"
                      :options="planStatusOptions"
                    />
                  </div>
                  <div class="admin-subscriptions__toolbar-actions">
                    <hs-button
                      variant="primary"
                      icon="plus"
                      @click="openCreatePlan"
                    >
                      New Plan
                    </hs-button>
                    <hs-button
                      variant="secondary"
                      :loading="loadingPlans"
                      icon="refresh-cw"
                      @click="refreshData"
                    >
                      Refresh
                    </hs-button>
                  </div>
                </div>
              </template>

              <div v-if="loadingPlans" class="admin-subscriptions__loading">
                <hs-skeleton variant="card" height="16rem" />
              </div>

              <hs-empty-state
                v-else-if="filteredPlans.length === 0"
                icon="clipboard"
                title="No plans match your filters"
                description="Create a new plan or clear your filters to review existing ones."
              />

              <table
                v-else
                class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
              >
                <thead class="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Plan
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Price
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Billing
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Features
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Updated
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="plan in filteredPlans" :key="plan.id">
                    <td class="px-6 py-4">
                      <div class="subscription-plan-table__name">
                        {{ plan.display_name }}
                      </div>
                      <div class="subscription-plan-table__meta">
                        {{ plan.name }} · ID {{ plan.id }}
                      </div>
                      <p v-if="plan.description" class="subscription-plan-table__description">
                        {{ plan.description }}
                      </p>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <strong class="subscription-plan-table__price">
                        {{ formatPlanPrice(plan) }}
                      </strong>
                      <div class="subscription-plan-table__meta">
                        {{ plan.currency || "RWF" }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                      {{ plan.billing_period_days }} days
                    </td>
                    <td class="px-6 py-4">
                      <div
                        v-if="plan.features && Object.keys(plan.features).length"
                        class="subscription-plan-table__features"
                      >
                        <hs-badge
                          v-for="(value, key) in plan.features"
                          :key="key"
                          tone="primary"
                          size="sm"
                        >
                          {{ key }}: {{ renderFeatureValue(value) }}
                        </hs-badge>
                      </div>
                      <span v-else class="subscription-plan-table__meta">No features</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <hs-badge :tone="plan.is_active ? 'success' : 'neutral'" size="sm">
                        {{ plan.is_active ? "Active" : "Inactive" }}
                      </hs-badge>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {{ formatDate(plan.updated_at) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <hs-button
                        variant="ghost"
                        size="sm"
                        icon="edit-2"
                        @click.stop="openEditPlan(plan)"
                      >
                        Edit
                      </hs-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </hs-table-shell>
          </div>

          <!-- Assign store -->
          <div v-else-if="activeTab === 'store-assignment'">
            <div class="admin-subscriptions__assignment-grid">
              <section class="admin-subscriptions__content-card">
                <h2>
                  Assign a plan to a store
                </h2>
                <p>
                  Search any store in the system, choose a plan, and capture
                  payment metadata. The assignment is super-admin only.
                </p>
                <hs-button
                  class="admin-subscriptions__primary-action"
                  icon="shopping-bag"
                  :loading="loadingPlans"
                  @click="openStoreAssignment"
                >
                  Assign to Store
                </hs-button>

                <hs-alert
                  v-if="storeAssignmentResult"
                  tone="success"
                  title="Last assignment"
                >
                  <template #default>
                    Store #{{ storeAssignmentResult.store_id }} is now on plan
                    <strong>{{
                      storeAssignmentResult.plan?.display_name ||
                      storeAssignmentResult.plan_id
                    }}</strong>
                    until {{ formatDate(storeAssignmentResult.expires_at) }}.
                  </template>
                </hs-alert>
              </section>

              <section class="admin-subscriptions__content-card">
                <h3>
                  Quick lookup
                </h3>
                <div class="admin-subscriptions__lookup-stack">
                  <div class="admin-subscriptions__lookup-row">
                    <hs-input
                      v-model="storeLookupId"
                      type="number"
                      label="Store ID"
                      placeholder="Enter store ID..."
                      @keyup.enter="loadStoreStatus"
                    />
                    <hs-button
                      variant="secondary"
                      icon="eye"
                      :loading="storeStatusLoading"
                      :disabled="storeStatusLoading"
                      @click="loadStoreStatus"
                    >
                      Preview
                    </hs-button>
                  </div>

                  <hs-alert
                    v-if="storeStatusError"
                    tone="danger"
                    title="Store lookup failed"
                    :message="storeStatusError"
                  />

                  <div
                    v-if="storeStatus"
                    class="admin-subscriptions__status-card"
                  >
                    <div class="admin-subscriptions__status-header">
                      <p>
                        Status: {{ storeStatus.status }}
                      </p>
                      <hs-badge
                        :tone="storeStatus.has_active_subscription || storeStatus.in_automatic_grace ? 'success' : 'danger'"
                      >
                        {{
                          storeStatus.has_active_subscription ||
                          storeStatus.in_automatic_grace
                            ? "Access granted"
                            : "Enforced"
                        }}
                      </hs-badge>
                    </div>
                    <hs-alert
                      v-if="storeStatus.in_automatic_grace"
                      tone="warning"
                      title="Automatic grace"
                      :message="storeStatus.grace_reason"
                    />
                    <div class="admin-subscriptions__status-meta">
                      <p v-if="storeStatus.plan">
                        Plan:
                        <strong>{{ storeStatus.plan.display_name }}</strong>
                      </p>
                      <p v-if="storeStatus.expires_at">
                        Expires on
                        {{ formatDate(storeStatus.expires_at) }} (grace ends
                        {{ formatDate(storeStatus.grace_period_ends_at) }})
                      </p>
                      <p v-if="storeStatus.trial_ends_at">
                        Trial ends {{ formatDate(storeStatus.trial_ends_at) }} •
                        Recent sale carts:
                        {{ storeStatus.sale_cart_count ?? "—" }}
                      </p>
                    </div>
                    <div
                      v-if="
                        storeStatus.features &&
                        Object.keys(storeStatus.features).length
                      "
                      class="admin-subscriptions__badge-row"
                    >
                      <hs-badge
                        v-for="(value, key) in storeStatus.features"
                        :key="key"
                        tone="primary"
                        size="sm"
                      >
                        {{ key }}: {{ renderFeatureValue(value) }}
                      </hs-badge>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Assign group -->
          <div v-else-if="activeTab === 'group-assignment'">
            <div class="admin-subscriptions__assignment-grid">
              <section class="admin-subscriptions__content-card">
                <h2>
                  Assign a plan to a group
                </h2>
                <p>
                  Groups combine multiple stores. Assigning a plan unlocks
                  features across their shared catalog and transfers.
                </p>
                <hs-button
                  class="admin-subscriptions__primary-action"
                  icon="share-2"
                  :loading="loadingPlans"
                  @click="openGroupAssignment"
                >
                  Assign to Group
                </hs-button>

                <hs-alert
                  v-if="groupAssignmentResult"
                  tone="success"
                  title="Last assignment"
                >
                  <template #default>
                    Group #{{ groupAssignmentResult.group_id }} now uses
                    <strong>{{
                      groupAssignmentResult.plan?.display_name ||
                      groupAssignmentResult.plan_id
                    }}</strong>
                    until {{ formatDate(groupAssignmentResult.expires_at) }}.
                  </template>
                </hs-alert>
              </section>

              <section class="admin-subscriptions__content-card">
                <h3>
                  Group subscription snapshot
                </h3>
                <div class="admin-subscriptions__lookup-stack">
                  <div class="admin-subscriptions__lookup-row">
                    <hs-input
                      v-model="groupLookupId"
                      type="number"
                      label="Group ID"
                      placeholder="Enter group ID..."
                      @keyup.enter="loadGroupStatus"
                    />
                    <hs-button
                      variant="secondary"
                      icon="eye"
                      :loading="groupStatusLoading"
                      :disabled="groupStatusLoading"
                      @click="loadGroupStatus"
                    >
                      Preview
                    </hs-button>
                  </div>
                </div>

                <hs-alert
                  v-if="groupStatusError"
                  tone="danger"
                  title="Group lookup failed"
                  :message="groupStatusError"
                />

                <div
                  v-if="groupStatus"
                  class="admin-subscriptions__status-card"
                >
                  <div class="admin-subscriptions__status-header">
                    <p>
                      Status: {{ groupStatus.status }}
                    </p>
                    <hs-badge :tone="groupStatus.has_active_subscription ? 'success' : 'danger'">
                      {{
                        groupStatus.has_active_subscription
                          ? "Active"
                          : "No plan"
                      }}
                    </hs-badge>
                  </div>
                  <div class="admin-subscriptions__status-meta">
                    <p v-if="groupStatus.plan">
                      Plan: <strong>{{ groupStatus.plan.display_name }}</strong>
                    </p>
                    <p v-if="groupStatus.expires_at">
                      Expires on {{ formatDate(groupStatus.expires_at) }}
                    </p>
                  </div>
                  <div
                    v-if="
                      groupStatus.features &&
                      Object.keys(groupStatus.features).length
                    "
                    class="admin-subscriptions__badge-row"
                  >
                    <hs-badge
                      v-for="(value, key) in groupStatus.features"
                      :key="key"
                      tone="primary"
                      size="sm"
                    >
                      {{ key }}: {{ renderFeatureValue(value) }}
                    </hs-badge>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Feature checks -->
          <div v-else-if="activeTab === 'feature-checks'">
            <subscription-feature-checks-panel :plans="plans" />
          </div>

          <div v-else-if="activeTab === 'messaging'">
            <subscription-messaging-panel />
          </div>

          <!-- System controls -->
          <div v-else-if="activeTab === 'system-controls'">
            <system-feature-flags-panel />
          </div>

          <!-- Store Subscriptions -->
          <div v-else-if="activeTab === 'store-subs'">
            <hs-table-shell
              title="Store subscriptions"
              description="Review assigned plans, renewal dates, and payment metadata across stores."
            >
              <template #actions>
                <hs-button
                  variant="primary"
                  icon="plus"
                  :loading="loadingPlans"
                  @click="openStoreAssignment"
                >
                  Assign Plan
                </hs-button>
              </template>
              <template #filters>
                <div class="admin-subscriptions__filters">
                  <hs-input
                v-model="storeSubsSearch"
                type="search"
                    icon="search"
                placeholder="Search by store name or ID..."
              />
                  <hs-select
                v-model="storeSubsStatusFilter"
                    class="admin-subscriptions__status-select"
                    :options="storeSubscriptionStatusOptions"
              />
                </div>
              </template>

              <!-- Loading -->
              <div
              v-if="loadingStoreSubs"
                class="admin-subscriptions__loading"
            >
                <hs-skeleton variant="card" height="16rem" />
            </div>

              <!-- Empty/No Results -->
              <hs-empty-state
              v-else-if="storeSubscriptions.length === 0"
                icon="shopping-bag"
                :title="
                  storeSubsSearch || storeSubsStatusFilter
                    ? 'No subscriptions match your filters'
                    : 'No store subscriptions yet'
                "
                description="Assigned store subscriptions will appear here."
              />

              <!-- Table -->
              <table
                v-else
                class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
              >
                <thead class="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Store
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Plan
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Starts
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Expires
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Payment
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                >
                  <tr v-for="sub in storeSubscriptions" :key="sub.id" :class="subscriptionRowClass(sub.display_status || sub.status)">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {{ sub.store_name || `Store #${sub.store_id}` }}
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">
                        {{ sub.store_merchant_code || `ID: ${sub.store_id}` }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900 dark:text-white">
                        {{ sub.plan?.display_name || "Unknown" }}
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">
                        {{ formatPlanPrice(sub.plan) }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <hs-badge :tone="statusTone(sub.display_status || sub.status)" size="sm">
                        {{ (sub.display_status || sub.status).replaceAll("_", " ") }}
                      </hs-badge>
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                    >
                      {{ formatDateTime(sub.starts_at) }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                    >
                      {{ formatDateTime(sub.expires_at) }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{
                        formatPaymentAmount(
                          sub.payment_amount,
                          sub.payment_currency
                        )
                      }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                    >
                      <hs-button
                        variant="ghost"
                        size="sm"
                        icon="eye"
                        @click="viewSubscriptionDetails(sub)"
                      >
                        View
                      </hs-button>
                      <hs-button v-if="sub.can_edit" variant="ghost" size="sm" icon="edit-2" @click="editingStoreSubscription = sub">
                        Edit
                      </hs-button>
                      <hs-button
                        v-if="sub.can_renew && sub.is_expired"
                        variant="ghost"
                        size="sm"
                        icon="refresh-cw"
                        :loading="renewingSubscriptionId === sub.id"
                        :disabled="renewingSubscriptionId !== null"
                        @click="renewStoreSubscription(sub)"
                      >
                        Renew
                      </hs-button>
                      <hs-action-menu
                        v-if="subscriptionStatusActions(sub).length"
                        :items="subscriptionStatusActions(sub)"
                        variant="ghost" label="Status"
                        @select="openSubscriptionStatus(sub, $event.value)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <template #footer>
              <pagination-controls
                v-if="storeSubsPagination.total > 0"
                :current-page="storeSubsPagination.page"
                :total-pages="storeSubsPagination.pages"
                :total="storeSubsPagination.total"
                :per-page="storeSubsPagination.perPage"
                :has-next="storeSubsPagination.hasNext"
                :has-prev="storeSubsPagination.hasPrev"
                items-label="subscriptions"
                @page-change="handleStoreSubsPageChange"
                @per-page-change="handleStoreSubsPerPageChange"
              />
              </template>
            </hs-table-shell>
          </div>

          <!-- Group Subscriptions -->
          <div v-else-if="activeTab === 'group-subs'">
            <hs-table-shell
              title="Group subscriptions"
              description="Review group-level assignments and payment metadata."
            >
              <template #filters>
                <div class="admin-subscriptions__filters">
                  <hs-input
                v-model="groupSubsSearch"
                type="search"
                    icon="search"
                placeholder="Search by group name or ID..."
              />
                  <hs-select
                v-model="groupSubsStatusFilter"
                    class="admin-subscriptions__status-select"
                    :options="subscriptionStatusOptions"
              />
                </div>
              </template>

            <!-- Loading -->
            <div
              v-if="loadingGroupSubs"
                class="admin-subscriptions__loading"
            >
                <hs-skeleton variant="card" height="16rem" />
            </div>

            <!-- Empty/No Results -->
              <hs-empty-state
              v-else-if="groupSubscriptions.length === 0"
                icon="users"
                :title="
                  groupSubsSearch || groupSubsStatusFilter
                    ? 'No subscriptions match your filters'
                    : 'No group subscriptions yet'
                "
                description="Assigned group subscriptions will appear here."
              />

            <!-- Table -->
              <table
                v-else
                class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
              >
                <thead class="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Group
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Plan
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Expires
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Payment
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                >
                  <tr v-for="sub in groupSubscriptions" :key="sub.id">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div
                        class="text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {{ sub.group_name || `Group #${sub.group_id}` }}
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">
                        ID: {{ sub.group_id }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900 dark:text-white">
                        {{ sub.plan?.display_name || "Unknown" }}
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">
                        {{ formatPlanPrice(sub.plan) }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <hs-badge :tone="statusTone(sub.status)" size="sm">
                        {{ sub.status }}
                      </hs-badge>
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                    >
                      {{ formatDate(sub.expires_at) }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{
                        formatPaymentAmount(
                          sub.payment_amount,
                          sub.payment_currency
                        )
                      }}
                    </td>
                    <td
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                    >
                      <hs-button
                        variant="ghost"
                        size="sm"
                        icon="eye"
                        @click="viewSubscriptionDetails(sub)"
                      >
                        View
                      </hs-button>
                      <hs-button v-if="sub.can_renew && sub.is_expired" variant="ghost" size="sm" icon="refresh-cw"
                        :loading="renewingGroupSubscriptionId === sub.id" :disabled="renewingGroupSubscriptionId !== null"
                        @click="renewGroupSubscription(sub)">Renew</hs-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <template #footer>
              <pagination-controls
                v-if="groupSubsPagination.total > 0"
                :current-page="groupSubsPagination.page"
                :total-pages="groupSubsPagination.pages"
                :total="groupSubsPagination.total"
                :per-page="groupSubsPagination.perPage"
                :has-next="groupSubsPagination.hasNext"
                :has-prev="groupSubsPagination.hasPrev"
                items-label="subscriptions"
                @page-change="handleGroupSubsPageChange"
                @per-page-change="handleGroupSubsPerPageChange"
              />
              </template>
            </hs-table-shell>
          </div>
        </div>
      </div>
    </div>

    <plan-form-modal
      v-if="showPlanModal"
      :plan="editingPlan"
      :loading="planSaving"
      :server-error="planSaveError"
      @close="closePlanModal"
      @save="handlePlanSave"
    />

    <subscription-assignment-modal
      v-if="showStoreAssignment"
      mode="STORE"
      :plans="plans"
      :loading="storeAssignmentLoading"
      :server-error="storeAssignmentError"
      @close="closeStoreAssignment"
      @assign="handleStoreAssignment"
    />

    <subscription-assignment-modal
      v-if="showGroupAssignment"
      mode="GROUP"
      :plans="plans"
      :loading="groupAssignmentLoading"
      :server-error="groupAssignmentError"
      @close="closeGroupAssignment"
      @assign="handleGroupAssignment"
    />

    <store-subscription-edit-modal
      v-if="editingStoreSubscription"
      :subscription="editingStoreSubscription"
      :plans="plans"
      @close="editingStoreSubscription = null"
      @saved="handleStoreSubscriptionSaved"
    />

    <subscription-status-modal
      v-if="statusChange"
      :subscription="statusChange.subscription" :action="statusChange.action"
      @close="statusChange = null" @saved="handleSubscriptionStatusSaved"
    />

    <subscription-details-modal
      v-if="showSubscriptionModal"
      :subscription="selectedSubscription"
      @close="showSubscriptionModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import PlanFormModal from "@/components/admin/subscriptions/PlanFormModal.vue";
import SubscriptionFeatureChecksPanel from "@/components/admin/subscriptions/SubscriptionFeatureChecksPanel.vue";
import SystemFeatureFlagsPanel from "@/components/admin/subscriptions/SystemFeatureFlagsPanel.vue";
import SubscriptionAssignmentModal from "@/components/admin/subscriptions/SubscriptionAssignmentModal.vue";
import SubscriptionMessagingPanel from "@/components/admin/subscriptions/SubscriptionMessagingPanel.vue";
import SubscriptionStatusModal from "@/components/admin/subscriptions/SubscriptionStatusModal.vue";
import StoreSubscriptionEditModal from "@/components/admin/subscriptions/StoreSubscriptionEditModal.vue";
import SubscriptionDetailsModal from "@/components/modals/SubscriptionDetailsModal.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import {
  HsActionMenu,
  HsAlert,
  HsBadge,
  HsButton,
  HsEmptyState,
  HsInput,
  HsMetricCard,
  HsSelect,
  HsSkeleton,
  HsTableShell,
  HsTabs,
} from "@/components/ui";

const tabs = [
  { id: "plans", label: "Plans", icon: "layers" },
  { id: "store-subs", label: "Store Subs", icon: "shopping-bag" },
  { id: "group-subs", label: "Group Subs", icon: "users" },
  { id: "store-assignment", label: "Assign Store", icon: "link" },
  { id: "group-assignment", label: "Assign Group", icon: "share-2" },
  { id: "feature-checks", label: "Feature Checks", icon: "check-circle" },
  { id: "messaging", label: "Messaging", icon: "mail" },
  { id: "system-controls", label: "System Controls", icon: "settings" },
];

export default {
  name: "AdminSubscriptionsView",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HsActionMenu,
    HsAlert,
    HsBadge,
    HsButton,
    HsEmptyState,
    HsInput,
    HsMetricCard,
    HsSelect,
    HsSkeleton,
    HsTableShell,
    HsTabs,
    PlanFormModal,
    SubscriptionFeatureChecksPanel,
    SystemFeatureFlagsPanel,
    SubscriptionAssignmentModal,
    SubscriptionDetailsModal,
    StoreSubscriptionEditModal,
    SubscriptionStatusModal,
    SubscriptionMessagingPanel,
    PaginationControls,
  },
  setup() {
    const store = useStore();
    const activeTab = ref("plans");
    const planSearch = ref("");
    const planStatusFilter = ref("all");
    const planStatusOptions = [
      { label: "All Plans", value: "all" },
      { label: "Active only", value: "active" },
      { label: "Inactive only", value: "inactive" },
    ];
    const subscriptionStatusOptions = [
      { label: "All Status", value: "" },
      { label: "Active", value: "ACTIVE" },
      { label: "Expired", value: "EXPIRED" },
      { label: "Cancelled", value: "CANCELLED" },
    ];

    const storeSubscriptionStatusOptions = [
      ...subscriptionStatusOptions,
      { label: "Grace period", value: "GRACE_PERIOD" },
      { label: "Scheduled", value: "SCHEDULED" },
      { label: "Suspended", value: "SUSPENDED" },
    ];

    const showPlanModal = ref(false);
    const planSaving = ref(false);
    const planSaveError = ref("");
    const editingPlan = ref(null);

    const showStoreAssignment = ref(false);
    const storeAssignmentLoading = ref(false);
    const storeAssignmentError = ref("");

    const showGroupAssignment = ref(false);
    const groupAssignmentLoading = ref(false);
    const groupAssignmentError = ref("");

    const showSubscriptionModal = ref(false);
    const selectedSubscription = ref(null);

    const storeLookupId = ref("");
    const groupLookupId = ref("");

    // Store Subscriptions
    const storeSubscriptions = ref([]);
    const loadingStoreSubs = ref(false);
    const storeSubsSearch = ref("");
    const storeSubsStatusFilter = ref("");
    const storeSubsPagination = ref({
      page: 1,
      perPage: 10,
      total: 0,
      pages: 0,
      hasNext: false,
      hasPrev: false,
    });

    // Group Subscriptions
    const groupSubscriptions = ref([]);
    const loadingGroupSubs = ref(false);
    const groupSubsSearch = ref("");
    const groupSubsStatusFilter = ref("");
    const groupSubsPagination = ref({
      page: 1,
      perPage: 10,
      total: 0,
      pages: 0,
      hasNext: false,
      hasPrev: false,
    });

    const plans = computed(
      () => store.getters["adminSubscriptions/plans"] || []
    );
    const loadingPlans = computed(
      () => store.getters["adminSubscriptions/loadingPlans"]
    );
    const storeStatus = computed(
      () => store.getters["adminSubscriptions/storeStatus"]
    );
    const storeStatusLoading = computed(
      () => store.getters["adminSubscriptions/storeStatusLoading"]
    );
    const storeStatusStoreError = computed(
      () => store.getters["adminSubscriptions/storeStatusError"]
    );
    const groupStatus = computed(
      () => store.getters["adminSubscriptions/groupStatus"]
    );
    const groupStatusLoading = computed(
      () => store.getters["adminSubscriptions/groupStatusLoading"]
    );
    const groupStatusStoreError = computed(
      () => store.getters["adminSubscriptions/groupStatusError"]
    );
    const storeAssignmentResult = computed(
      () => store.getters["adminSubscriptions/lastStoreAssignment"]
    );
    const groupAssignmentResult = computed(
      () => store.getters["adminSubscriptions/lastGroupAssignment"]
    );

    const storeStatusValidationError = ref("");
    const groupStatusValidationError = ref("");

    const storeStatusError = computed(
      () =>
        storeStatusValidationError.value || storeStatusStoreError.value || ""
    );
    const groupStatusError = computed(
      () =>
        groupStatusValidationError.value || groupStatusStoreError.value || ""
    );

    const loadPlans = async () => {
      try {
        await store.dispatch("adminSubscriptions/fetchPlans");
      } catch (error) {
        console.error("Failed to load plans", error);
        toast.error("Unable to load subscription plans.");
      }
    };

    const refreshData = () => {
      loadPlans();
      if (storeLookupId.value) {
        loadStoreStatus();
      }
      if (groupLookupId.value) {
        loadGroupStatus();
      }
    };

    const filteredPlans = computed(() => {
      const search = planSearch.value.trim().toLowerCase();
      return plans.value.filter((plan) => {
        const matchesSearch =
          !search ||
          plan.name?.toLowerCase().includes(search) ||
          plan.display_name?.toLowerCase().includes(search) ||
          plan.description?.toLowerCase().includes(search);

        const matchesStatus =
          planStatusFilter.value === "all" ||
          (planStatusFilter.value === "active" && plan.is_active) ||
          (planStatusFilter.value === "inactive" && !plan.is_active);

        return matchesSearch && matchesStatus;
      });
    });

    const summaryStats = computed(() => {
      const total = plans.value.length;
      const active = plans.value.filter((plan) => plan.is_active).length;
      const freePlans = plans.value.filter(
        (plan) => !plan.price_amount || plan.price_amount === 0
      ).length;
      const monthlyRange = plans.value
        .filter(
          (plan) => Number(plan.billing_period_days) === 30 && plan.price_amount
        )
        .map((plan) => plan.price_amount);

      let priceLabel = "—";
      if (monthlyRange.length) {
        const min = Math.min(...monthlyRange);
        const max = Math.max(...monthlyRange);
        if (min === max) {
          priceLabel = formatCurrency(min);
        } else {
          priceLabel = `${formatCurrency(min)} – ${formatCurrency(max)}`;
        }
      }

      return [
        {
          label: "Total Plans",
          value: total,
          caption: "All tiers available to stores and groups",
          icon: "layers",
          tone: "primary",
        },
        {
          label: "Active Plans",
          value: active,
          caption: "Published plans visible to admins",
          icon: "check-circle",
          tone: "success",
        },
        {
          label: "Free Tiers",
          value: freePlans,
          caption: "Plans with price set to 0",
          icon: "gift",
          tone: "info",
        },
        {
          label: "Monthly Price Range",
          value: priceLabel,
          caption: "Based on 30 day billing cycles",
          icon: "credit-card",
          tone: "warning",
        },
      ];
    });

    const formatPlanPrice = (plan) => {
      if (!plan.price_amount || plan.price_amount === 0) {
        return "Free";
      }
      const currency = plan.currency || "RWF";
      const amount = Number(plan.price_amount);
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${amount} ${currency}`;
      }
    };

    const renderFeatureValue = (value) => {
      if (typeof value === "boolean") {
        return value ? "Enabled" : "Disabled";
      }
      if (value === null || value === undefined) {
        return "—";
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return value;
    };

    const formatDateTime = (value) => {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    };

    const formatDate = (value) => {
      if (!value) {
        return "—";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatPaymentAmount = (amount, currency) => {
      if (!amount || amount === 0) {
        return "—";
      }

      // Amounts are stored in the main currency unit.
      const formattedAmount = Number(amount).toLocaleString();
      const currencySymbol = currency === "RWF" ? "RF" : currency;

      return `${currencySymbol} ${formattedAmount}`;
    };

    const renewingSubscriptionId = ref(null);
    const renewStoreSubscription = async (subscription) => {
      if (renewingSubscriptionId.value !== null) return;
      renewingSubscriptionId.value = subscription.id;
      try {
        await http.post(`/v1/admin/subscriptions/stores/${subscription.id}/renew`);
        toast.success(`Subscription renewed for ${subscription.store_name || `Store #${subscription.store_id}`}.`);
        await loadStoreSubscriptions();
        if (Number(storeLookupId.value) === subscription.store_id) await loadStoreStatus();
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Unable to renew subscription.");
      } finally {
        renewingSubscriptionId.value = null;
      }
    };

    const renewingGroupSubscriptionId = ref(null);
    const renewGroupSubscription = async (subscription) => {
      if (renewingGroupSubscriptionId.value !== null) return;
      renewingGroupSubscriptionId.value = subscription.id;
      try {
        await http.post(`/v1/admin/subscriptions/groups/${subscription.id}/renew`);
        toast.success("Group subscription renewed.");
        await loadGroupSubscriptions();
        if (Number(groupLookupId.value) === subscription.group_id) await loadGroupStatus();
      } catch (error) { toast.error(error?.response?.data?.error?.message || "Unable to renew group subscription."); }
      finally { renewingGroupSubscriptionId.value = null; }
    };

    const statusChange = ref(null);
    const subscriptionStatusActions = (sub) => [
      ...(sub.can_suspend ? [{ label: "Suspend", value: "SUSPENDED", icon: "pause-circle" }] : []),
      ...(sub.can_resume ? [{ label: "Resume", value: "ACTIVE", icon: "play-circle" }] : []),
      ...(sub.can_cancel ? [{ label: "Cancel subscription", value: "CANCELLED", icon: "x-circle", tone: "danger" }] : []),
    ];
    const openSubscriptionStatus = (subscription, action) => { statusChange.value = { subscription, action }; };
    const handleSubscriptionStatusSaved = async (updated) => {
      statusChange.value = null;
      toast.success("Subscription status updated.");
      if (selectedSubscription.value?.id === updated.id) selectedSubscription.value = { ...selectedSubscription.value, ...updated };
      await loadStoreSubscriptions();
      if (Number(storeLookupId.value) === updated.store_id) await loadStoreStatus();
    };

    const editingStoreSubscription = ref(null);
    const handleStoreSubscriptionSaved = async () => {
      editingStoreSubscription.value = null;
      toast.success("Store subscription updated.");
      await loadStoreSubscriptions();
      if (storeLookupId.value) await loadStoreStatus();
    };

    const viewSubscriptionDetails = (subscription) => {
      selectedSubscription.value = subscription;
      showSubscriptionModal.value = true;
    };

    const openCreatePlan = () => {
      editingPlan.value = null;
      planSaveError.value = "";
      showPlanModal.value = true;
    };

    const openStoreAssignment = async () => {
      if (!plans.value.length) {
        await loadPlans();
      }
      showStoreAssignment.value = true;
    };

    const openGroupAssignment = async () => {
      if (!plans.value.length) {
        await loadPlans();
      }
      showGroupAssignment.value = true;
    };

    const openEditPlan = (plan) => {
      editingPlan.value = plan;
      planSaveError.value = "";
      showPlanModal.value = true;
    };

    const closePlanModal = () => {
      showPlanModal.value = false;
      editingPlan.value = null;
      planSaveError.value = "";
    };

    const handlePlanSave = async (payload) => {
      planSaveError.value = "";
      planSaving.value = true;
      try {
        if (editingPlan.value) {
          // Update existing plan
          const response = await store.dispatch(
            "adminSubscriptions/updatePlan",
            {
              planId: editingPlan.value.id,
              payload,
            }
          );
          toast.success(
            `Plan ${response?.display_name || payload.display_name} updated.`
          );
        } else {
          // Create new plan
          const response = await store.dispatch(
            "adminSubscriptions/createPlan",
            payload
          );
          toast.success(
            `Plan ${response?.display_name || payload.display_name} created.`
          );
        }
        showPlanModal.value = false;
        editingPlan.value = null;
        await loadPlans();
      } catch (error) {
        console.error("Failed to save plan", error);
        planSaveError.value =
          error?.response?.data?.error?.message ||
          "Unable to save plan. Please try again.";
      } finally {
        planSaving.value = false;
      }
    };

    const closeStoreAssignment = () => {
      showStoreAssignment.value = false;
      storeAssignmentError.value = "";
    };

    const handleStoreAssignment = async (payload) => {
      const { entity_id: storeId, ...body } = payload;
      if (!storeId) {
        storeAssignmentError.value = "Missing store identifier.";
        return;
      }

      storeAssignmentLoading.value = true;
      storeAssignmentError.value = "";
      try {
        await store.dispatch("adminSubscriptions/assignStoreSubscription", {
          storeId,
          body,
        });
        toast.success(`Assigned plan to store #${storeId}.`);
        showStoreAssignment.value = false;
        await loadStoreSubscriptions();
        if (
          storeLookupId.value &&
          Number(storeLookupId.value) === Number(storeId)
        ) {
          await loadStoreStatus();
        }
      } catch (error) {
        console.error("Failed to assign plan to store", error);
        storeAssignmentError.value =
          error?.response?.data?.error?.message ||
          "Unable to assign plan to store. Please check your permissions.";
      } finally {
        storeAssignmentLoading.value = false;
      }
    };

    const closeGroupAssignment = () => {
      showGroupAssignment.value = false;
      groupAssignmentError.value = "";
    };

    const handleGroupAssignment = async (payload) => {
      const { entity_id: groupId, ...body } = payload;
      if (!groupId) {
        groupAssignmentError.value = "Missing group identifier.";
        return;
      }

      groupAssignmentLoading.value = true;
      groupAssignmentError.value = "";
      try {
        const response = await http.post(
          `/v1/admin/subscriptions/group/${groupId}`,
          body
        );
        groupAssignmentResult.value = response?.data || null;
        toast.success(`Assigned plan to group #${groupId}.`);
        showGroupAssignment.value = false;
        if (
          groupLookupId.value &&
          Number(groupLookupId.value) === Number(groupId)
        ) {
          await loadGroupStatus();
        }
      } catch (error) {
        console.error("Failed to assign plan to group", error);
        groupAssignmentError.value =
          error?.response?.data?.error?.message ||
          "Unable to assign plan to group. Please try again.";
      } finally {
        groupAssignmentLoading.value = false;
      }
    };

    const loadStoreStatus = async () => {
      const storeId = Number(storeLookupId.value);
      if (!storeId) {
        storeStatusValidationError.value = "Provide a valid store ID.";
        return;
      }

      storeStatusValidationError.value = "";
      try {
        await store.dispatch("adminSubscriptions/fetchStoreStatus", storeId);
      } catch (error) {
        console.error("Failed to load store subscription status", error);
        // Error is already handled by the Vuex action
      }
    };

    const loadGroupStatus = async () => {
      const groupId = Number(groupLookupId.value);
      if (!groupId) {
        groupStatusValidationError.value = "Provide a valid group ID.";
        return;
      }

      groupStatusValidationError.value = "";
      try {
        await store.dispatch("adminSubscriptions/fetchGroupStatus", groupId);
      } catch (error) {
        console.error("Failed to load group subscription status", error);
        // Error is already handled by the Vuex action
      }
    };

    const loadStoreSubscriptions = async () => {
      loadingStoreSubs.value = true;
      try {
        const params = {
          page: storeSubsPagination.value.page,
          per_page: storeSubsPagination.value.perPage,
        };

        if (storeSubsSearch.value) {
          params.search_query = storeSubsSearch.value;
          params.search_by = "store_name,store_id";
        }

        if (storeSubsStatusFilter.value) {
          params.status = storeSubsStatusFilter.value;
        }

        const data = await store.dispatch(
          "adminSubscriptions/fetchStoreSubscriptions",
          params
        );

        storeSubscriptions.value = data?.items || [];
        storeSubsPagination.value = {
          page: data?.page || 1,
          perPage: data?.per_page || 10,
          total: data?.total || 0,
          pages: data?.pages || 0,
          hasNext: data?.has_next || false,
          hasPrev: data?.has_prev || false,
        };
      } catch (error) {
        console.error("Failed to load store subscriptions", error);
        toast.error("Unable to load store subscriptions.");
      } finally {
        loadingStoreSubs.value = false;
      }
    };

    const handleStoreSubsPageChange = (page) => {
      storeSubsPagination.value.page = page;
      loadStoreSubscriptions();
    };

    const handleStoreSubsPerPageChange = (perPage) => {
      storeSubsPagination.value.perPage = perPage;
      storeSubsPagination.value.page = 1;
      loadStoreSubscriptions();
    };

    const loadGroupSubscriptions = async () => {
      loadingGroupSubs.value = true;
      try {
        const params = {
          page: groupSubsPagination.value.page,
          per_page: groupSubsPagination.value.perPage,
        };

        if (groupSubsSearch.value) {
          params.search_query = groupSubsSearch.value;
          params.search_by = "group_name,group_id";
        }

        if (groupSubsStatusFilter.value) {
          params.status = groupSubsStatusFilter.value;
        }

        const data = await store.dispatch(
          "adminSubscriptions/fetchGroupSubscriptions",
          params
        );

        groupSubscriptions.value = data?.items || [];
        groupSubsPagination.value = {
          page: data?.page || 1,
          perPage: data?.per_page || 10,
          total: data?.total || 0,
          pages: data?.pages || 0,
          hasNext: data?.has_next || false,
          hasPrev: data?.has_prev || false,
        };
      } catch (error) {
        console.error("Failed to load group subscriptions", error);
        toast.error("Unable to load group subscriptions.");
      } finally {
        loadingGroupSubs.value = false;
      }
    };

    const handleGroupSubsPageChange = (page) => {
      groupSubsPagination.value.page = page;
      loadGroupSubscriptions();
    };

    const handleGroupSubsPerPageChange = (perPage) => {
      groupSubsPagination.value.perPage = perPage;
      groupSubsPagination.value.page = 1;
      loadGroupSubscriptions();
    };

    const getStatusClass = (status) => {
      const classes = {
        ACTIVE:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        EXPIRED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
        CANCELLED:
          "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
      };
      return classes[status] || classes.CANCELLED;
    };

    const subscriptionRowClass = (status) => `subscription-row--${String(status || "").toLowerCase()}`;

    const statusTone = (status) => {
      const normalized = String(status || "").toUpperCase();
      if (normalized === "ACTIVE") {
        return "success";
      }
      if (normalized === "GRACE_PERIOD" || normalized === "SUSPENDED") return "warning";
      if (normalized === "SCHEDULED") return "info";
      if (normalized === "EXPIRED") {
        return "danger";
      }
      return "neutral";
    };

    // Watch for filter changes
    watch([storeSubsSearch, storeSubsStatusFilter], () => {
      storeSubsPagination.value.page = 1;
      if (activeTab.value === "store-subs") {
        loadStoreSubscriptions();
      }
    });

    watch([groupSubsSearch, groupSubsStatusFilter], () => {
      groupSubsPagination.value.page = 1;
      if (activeTab.value === "group-subs") {
        loadGroupSubscriptions();
      }
    });

    // Load subscriptions when tab changes
    watch(activeTab, (newTab) => {
      if (newTab === "store-subs" && storeSubscriptions.value.length === 0) {
        loadStoreSubscriptions();
      } else if (
        newTab === "group-subs" &&
        groupSubscriptions.value.length === 0
      ) {
        loadGroupSubscriptions();
      }
    });

    onMounted(() => {
      loadPlans();
    });

    return {
      tabs,
      activeTab,
      plans,
      loadingPlans,
      planSearch,
      planStatusFilter,
      planStatusOptions,
      subscriptionStatusOptions,
      storeSubscriptionStatusOptions,
      filteredPlans,
      summaryStats,
      renderFeatureValue,
      formatPlanPrice,
      formatDate,
      formatDateTime,
      formatPaymentAmount,
      showPlanModal,
      editingPlan,
      planSaving,
      planSaveError,
      openCreatePlan,
      openStoreAssignment,
      openGroupAssignment,
      openEditPlan,
      closePlanModal,
      handlePlanSave,
      refreshData,

      showStoreAssignment,
      storeAssignmentLoading,
      storeAssignmentError,
      storeAssignmentResult,
      closeStoreAssignment,
      handleStoreAssignment,

      showGroupAssignment,
      groupAssignmentLoading,
      groupAssignmentError,
      groupAssignmentResult,
      closeGroupAssignment,
      handleGroupAssignment,

      storeLookupId,
      storeStatusLoading,
      storeStatusError,
      storeStatus,
      loadStoreStatus,

      groupLookupId,
      groupStatusLoading,
      groupStatusError,
      groupStatus,
      loadGroupStatus,

      storeSubscriptions,
      loadingStoreSubs,
      storeSubsSearch,
      storeSubsStatusFilter,
      storeSubsPagination,
      loadStoreSubscriptions,
      handleStoreSubsPageChange,
      handleStoreSubsPerPageChange,

      groupSubscriptions,
      loadingGroupSubs,
      groupSubsSearch,
      groupSubsStatusFilter,
      groupSubsPagination,
      loadGroupSubscriptions,
      handleGroupSubsPageChange,
      handleGroupSubsPerPageChange,

      getStatusClass,
      statusTone,
      subscriptionRowClass,

      showSubscriptionModal,
      selectedSubscription,
      viewSubscriptionDetails,
      editingStoreSubscription,
      statusChange,
      subscriptionStatusActions,
      openSubscriptionStatus,
      handleSubscriptionStatusSaved,
      renewingSubscriptionId,
      renewingGroupSubscriptionId,
      renewGroupSubscription,
      renewStoreSubscription,
      handleStoreSubscriptionSaved,
    };
  },
};

function formatCurrency(amount) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "RWF",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${amount} RWF`;
  }
}
</script>

<style scoped>
.subscription-row--active { background-color: rgba(16, 185, 129, 0.07); }
.subscription-row--expired { background-color: rgba(239, 68, 68, 0.07); }
.subscription-row--grace_period { background-color: rgba(245, 158, 11, 0.10); }
.subscription-row--scheduled { background-color: rgba(59, 130, 246, 0.07); }
.subscription-row--cancelled,
.subscription-row--suspended { background-color: rgba(148, 163, 184, 0.10); }

.admin-subscriptions {
  display: grid;
  gap: 1rem;
}

.admin-subscriptions__topbar {
  display: flex;
  justify-content: flex-end;
}

.admin-subscriptions__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.admin-subscriptions__body {
  display: grid;
  gap: 1rem;
}

.admin-subscriptions__stats {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.admin-subscriptions__panel {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.admin-subscriptions__tab-panel,
.admin-subscriptions__toolbar,
.admin-subscriptions__assignment-grid,
.admin-subscriptions__lookup-stack {
  display: grid;
  gap: 1rem;
}

.admin-subscriptions__toolbar {
  align-items: center;
  grid-template-columns: minmax(0, 1fr) minmax(14rem, 24rem) auto;
}

.admin-subscriptions__filters {
  align-items: end;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: minmax(16rem, 1fr) minmax(11rem, 14rem);
}

.admin-subscriptions__toolbar-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: flex-end;
}

.admin-subscriptions__status-select {
  min-width: 0;
}

.admin-subscriptions__content-card > p,
.subscription-plan-card__description,
.subscription-plan-card__meta,
.admin-subscriptions__status-meta {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.55;
  margin: 0;
}

.subscriptions-plans-grid {
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.subscription-plan-card,
.admin-subscriptions__content-card,
.admin-subscriptions__status-card {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 96%, var(--hs-bg-soft, #f8fafc));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  min-width: 0;
}

.subscription-plan-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.subscription-plan-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 34%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.07);
}

.subscription-plan-card__body {
  display: grid;
  gap: 0.8rem;
}

.subscription-plan-card__header,
.subscription-plan-card__footer,
.admin-subscriptions__status-header {
  align-items: flex-start;
  display: flex;
  gap: 0.85rem;
  justify-content: space-between;
}

.subscription-plan-card__title,
.admin-subscriptions__content-card h2,
.admin-subscriptions__content-card h3,
.admin-subscriptions__status-header p {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  line-height: 1.25;
  margin: 0;
}

.subscription-plan-card__title {
  font-size: 1rem;
}

.subscription-plan-card__code {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 900;
  margin: 0.2rem 0 0;
  text-transform: uppercase;
}

.subscription-plan-card__price-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.subscription-plan-card__price-row span {
  color: var(--hs-primary, #3293ec);
  font-size: 0.94rem;
  font-weight: 950;
}

.subscription-plan-card__price-row small {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 800;
}

.subscription-plan-card__features,
.admin-subscriptions__badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.subscription-plan-card__footer {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  margin-top: 1rem;
  padding-top: 0.8rem;
}

.subscription-plan-card__meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.subscription-plan-table__name,
.subscription-plan-table__price {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
}

.subscription-plan-table__name {
  font-size: 0.9rem;
}

.subscription-plan-table__price {
  color: var(--hs-primary, #3293ec);
  font-size: 0.88rem;
}

.subscription-plan-table__meta,
.subscription-plan-table__description {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 750;
  line-height: 1.45;
  margin: 0.2rem 0 0;
}

.subscription-plan-table__description {
  max-width: 28rem;
  overflow-wrap: anywhere;
}

.subscription-plan-table__features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  max-width: 28rem;
}

.admin-subscriptions__assignment-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.admin-subscriptions__content-card {
  align-content: start;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.admin-subscriptions__content-card h2 {
  font-size: 1rem;
}

.admin-subscriptions__content-card h3 {
  font-size: 0.86rem;
  text-transform: uppercase;
}

.admin-subscriptions__primary-action {
  justify-self: start;
}

.admin-subscriptions__lookup-row {
  align-items: end;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.admin-subscriptions__status-card {
  display: grid;
  gap: 0.75rem;
  padding: 0.9rem;
}

.admin-subscriptions__status-meta {
  display: grid;
  gap: 0.25rem;
}

.admin-subscriptions__loading {
  padding: 1rem;
}

.admin-subscriptions__panel :deep(.ui-table-shell) {
  border-radius: 14px;
  box-shadow: none;
}

.admin-subscriptions__panel :deep(th) {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 70%, var(--hs-surface, #ffffff));
}

.admin-subscriptions__panel :deep(td),
.admin-subscriptions__panel :deep(th) {
  border-color: var(--hs-border, rgba(148, 163, 184, 0.18));
}

.admin-subscriptions__panel :deep(.empty-state) {
  border: 0;
  box-shadow: none;
}

@media (max-width: 980px) {
  .admin-subscriptions__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-subscriptions__toolbar,
  .admin-subscriptions__assignment-grid {
    grid-template-columns: 1fr;
  }

  .admin-subscriptions__toolbar-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .admin-subscriptions__stats {
    grid-template-columns: 1fr;
  }

  .admin-subscriptions__filters,
  .subscriptions-plans-grid,
  .admin-subscriptions__lookup-row {
    grid-template-columns: 1fr;
  }

  .subscription-plan-card__header,
  .subscription-plan-card__footer,
  .admin-subscriptions__status-header {
    flex-direction: column;
  }

  .admin-subscriptions__actions,
  .admin-subscriptions__actions :deep(.ui-button),
  .admin-subscriptions__toolbar-actions,
  .admin-subscriptions__toolbar-actions :deep(.ui-button) {
    width: 100%;
  }

  .admin-subscriptions__lookup-row :deep(.ui-button),
  .admin-subscriptions__primary-action {
    justify-content: center;
    width: 100%;
  }
}
</style>
